import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { admin_apiConfig } from 'shared/config/config';
import { authFetch } from 'shared/api/authFetch';
import { readSseEvents } from 'shared/api/consumeSseStream';
import { useAdminAccount } from 'features/hooks/account';
import { useUserOrgId } from 'shared/session';
import { useAuth } from 'app/providers/AuthProvider';
import {
  applyViewAuthoringToolComplete,
  buildViewAuthoringPreviewStateFromEnvironmentSnapshot,
  createViewAuthoringPreviewState,
  materializeViewAuthoringPreview,
} from './viewAuthoringPreviewState';
import {
  finalizeRunningProcessBlocks,
  normalizeTimelineBlock,
  toolCallIdsMatch,
  toRenderableText,
} from './chatHistoryNormalization';
import { deviceTimeZone } from 'shared/time/businessTime';

// ── Block helpers (pure functions) ─────────────────────────────────────

const makeBlockId = () => `block-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
const VIEW_AUTHORING_CONTEXT_PAGES = new Set(['views', 'view-editor']);

const isViewAuthoringContextPage = (activePage) =>
  VIEW_AUTHORING_CONTEXT_PAGES.has(String(activePage || '').trim().toLowerCase());

const addDeltaBlock = (blocks, blockType, delta) => {
  if (!delta) return blocks;
  const next = Array.isArray(blocks) ? [...blocks] : [];
  const last = next[next.length - 1];
  if (last && last.type === blockType && (blockType === 'text' || blockType === 'thinking')) {
    next[next.length - 1] = { ...last, text: `${last.text || ''}${delta}` };
    return next;
  }
  return [...next, { id: makeBlockId(), type: blockType, text: delta }];
};

const addToolCallBlock = (blocks, payload) => [
  ...(Array.isArray(blocks) ? blocks : []),
  {
    id: makeBlockId(),
    type: 'tool_call',
    toolName: payload.toolName || 'tool',
    toolCallId: payload.toolCallId || null,
    args: payload.args || {},
    reasoning: payload.reasoning || '',
    status: 'running',
    result: null,
  },
];

const addNoticeBlock = (blocks, payload) => [
  ...(Array.isArray(blocks) ? blocks : []),
  {
    id: makeBlockId(),
    type: 'notice',
    text:
      payload?.message ||
      (payload?.type === 'context_window_exceeded'
        ? 'The conversation context has grown too large. Please start a new chat or ask a more focused question.'
        : 'Earlier messages were summarized so the conversation stays quick. They are all still here to read.'),
    meta: {
      kind:
        payload?.kind ??
        (payload?.type === 'model_fallback_notice'
          ? 'model_fallback'
          : payload?.type === 'context_window_exceeded'
            ? 'context_window_exceeded'
            : 'context_compacted'),
      compactionId: payload?.compaction_id ?? null,
      collapsedMessages: payload?.collapsed_messages ?? null,
      summaryChars: payload?.summary_chars ?? null,
      beforeTokensEst: payload?.before_tokens_est ?? null,
      afterTokensEst: payload?.after_tokens_est ?? null,
      childSessionId: payload?.child_session_id ?? null,
      fromProvider: payload?.from_provider ?? null,
      toProvider: payload?.to_provider ?? null,
    },
  },
];

const SERVER_STATUS_MESSAGES = {
  workspace_loading_start: 'Preparing workspace…',
  session_snapshot_restore_start: 'Restoring session workspace…',
  sandbox_restore_start: 'Restoring sandbox checkpoint…',
  sandbox_hydration_start: 'Loading sandbox data…',
  sandbox_hydration_complete: 'Sandbox data loaded.',
  sandbox_read_index_start: 'Applying sandbox changes…',
  sandbox_read_index_complete: 'Sandbox changes applied.',
  sandbox_restore_complete: 'Sandbox restore complete.',
  runtime_base_hydration_start: 'Loading organization runtime…',
  workspace_loading_complete: 'Workspace ready.',
  prompt_build_start: 'Building agent prompt…',
  llm_vision_request: 'Sending drawing page images to Grok…',
  workspace_cache_hit: 'Using cached workspace.',
};

const HYDRATION_PROGRESS_MESSAGES = {
  overlay_snapshot_lookup: 'Checking saved sandbox snapshot…',
  overlay_snapshot_download: 'Loading saved sandbox snapshot…',
  overlay_snapshot_applied: 'Restored sandbox snapshot from checkpoint.',
  redis_snapshot_lookup: 'Checking hydrated sandbox cache…',
  redis_snapshot_download: 'Loading hydrated sandbox cache…',
  redis_snapshot_applied: 'Restored sandbox from cache.',
  cache_miss: 'Refreshing sandbox data from database…',
  live_query_applied: 'Sandbox data refreshed from database.',
};

const formatServerStatusMessage = (payload) => {
  if (payload?.message) return payload.message;
  const phase = payload?.phase;
  if (payload?.type === 'hydration_progress') {
    return HYDRATION_PROGRESS_MESSAGES[phase] || 'Loading sandbox data…';
  }
  return SERVER_STATUS_MESSAGES[phase] || 'Working…';
};

const stripTransientStatusBlocks = (blocks) =>
  (Array.isArray(blocks) ? blocks : []).filter(
    (block) => !(block?.type === 'notice' && block?.meta?.transient)
  );

const upsertTransientStatusBlock = (blocks, payload) => {
  const nextBlocks = Array.isArray(blocks) ? [...blocks] : [];
  const statusIndex = nextBlocks.findIndex(
    (block) => block?.type === 'notice' && block?.meta?.kind === 'server_status'
  );
  const statusBlock = {
    id: statusIndex >= 0 ? nextBlocks[statusIndex].id : makeBlockId(),
    type: 'notice',
    text: formatServerStatusMessage(payload),
    meta: {
      kind: 'server_status',
      phase: payload?.phase || null,
      transient: true,
      cacheHit: payload?.cache_hit ?? payload?.hydration?.cache_hit ?? null,
      cacheSource: payload?.cache_source ?? payload?.hydration?.cache_source ?? null,
    },
  };
  if (statusIndex >= 0) {
    nextBlocks[statusIndex] = statusBlock;
    return nextBlocks;
  }
  return [...nextBlocks, statusBlock];
};

const completeToolCallBlock = (blocks, payload) => {
  if (!Array.isArray(blocks)) return [];
  let updated = false;
  return blocks.map((block) => {
    if (!updated && block.type === 'tool_call' && block.status === 'running') {
      const matchesById =
        payload.toolCallId != null &&
        block.toolCallId != null &&
        toolCallIdsMatch(payload.toolCallId, block.toolCallId);
      const matchesByName =
        payload.toolCallId == null &&
        block.toolName === payload.toolName;
      if (matchesById || matchesByName) {
        updated = true;
        return { ...block, status: 'completed', result: payload.result };
      }
    }
    return block;
  });
};

const notifyToolComplete = (onToolCompleteRef, toolPayload) => {
  try {
    Promise.resolve(onToolCompleteRef.current?.(toolPayload)).catch(() => {
      // Async preview authorization must not interrupt the conversation either.
    });
  } catch (_) {
    // Preview side-effects must not break chat streaming.
  }
};

// ── Timeline → messages conversion ────────────────────────────────────

const getEventBlocks = (event) => {
  if (!event) return [];
  if (Array.isArray(event.blocks)) return event.blocks;
  return event.content && Array.isArray(event.content.blocks) ? event.content.blocks : [];
};

const timelineEventToMessage = (event) => {
  const rawBlocks = getEventBlocks(event);
  const blocks = Array.isArray(rawBlocks)
    ? rawBlocks.map((block) => normalizeTimelineBlock(block, makeBlockId)).filter(Boolean)
    : [];
  const blockTextContent = blocks
    .filter((b) => b.type === 'text')
    .map((b) => toRenderableText(b.text))
    .join('\n')
    .trim();
  return {
    id: event.id || `event-${event.seq}`,
    role: event.actor_type === 'user' ? 'user' : 'assistant',
    isError: event.event_type === 'error',
    content: blockTextContent || toRenderableText(event?.content),
    blocks,
    timestamp: event.timestamp || new Date().toISOString(),
    metadata: {
      event_type: event.event_type,
      provider: event.provider,
      model: event.model,
      actor_type: event.actor_type,
      run_id: event.run_id || null,
      input_id: event.input_id || event.content?.input_id || event.metadata?.input_id || null,
      delivery_status: event.actor_type === 'user' ? 'received' : undefined,
    },
  };
};

const timelineToMessages = (timeline) => {
  if (!Array.isArray(timeline) || timeline.length === 0) return [];
  const messages = [];
  let assistantAccumulator = null;

  const flushAssistant = () => {
    if (!assistantAccumulator) return;
    const { blocks, timestamp, firstEvent } = assistantAccumulator;
    const content =
      blocks
        .filter((b) => b.type === 'text')
        .map((b) => toRenderableText(b.text))
        .join('\n')
        .trim() || toRenderableText(firstEvent?.content);
    const toolCallsFromBlocks = blocks
      .filter((b) => b.type === 'tool_call')
      .map((b) => ({ name: b.toolName, status: b.status || 'completed', reasoning: b.reasoning }));
    messages.push({
      id: firstEvent.id || `event-${firstEvent.seq}`,
      role: 'assistant',
      content,
      blocks,
      timestamp: timestamp || new Date().toISOString(),
      toolCalls: toolCallsFromBlocks.length > 0 ? toolCallsFromBlocks : undefined,
      metadata: {
        event_type: firstEvent.event_type,
        provider: firstEvent.provider,
        model: firstEvent.model,
        actor_type: 'assistant',
        run_id: firstEvent.run_id || null,
      },
    });
    assistantAccumulator = null;
  };

  for (const event of timeline) {
    const actorType = event.actor_type || event.actorType;
    if (actorType === 'system') continue;

    if (actorType === 'user') {
      flushAssistant();
      messages.push(timelineEventToMessage(event));
      continue;
    }

    if (event.event_type === 'error') {
      flushAssistant();
      messages.push(timelineEventToMessage(event));
      continue;
    }

    const rawBlocks = getEventBlocks(event);
    const blocks = Array.isArray(rawBlocks)
      ? rawBlocks.map((block) => normalizeTimelineBlock(block, makeBlockId)).filter(Boolean)
      : [];
    if (blocks.length === 0 && actorType === 'tool') continue;

    if (!assistantAccumulator) {
      assistantAccumulator = {
        blocks: [...blocks],
        seenToolCallIds: new Set(),
        timestamp: event.timestamp || new Date().toISOString(),
        firstEvent: event,
        runId: event.run_id || null,
      };
      for (const b of assistantAccumulator.blocks) {
        if (b?.type === 'tool_call' && b.toolCallId) assistantAccumulator.seenToolCallIds.add(String(b.toolCallId));
      }
    } else {
      if ((event.run_id || null) !== assistantAccumulator.runId) {
        flushAssistant();
        assistantAccumulator = {
          blocks: [...blocks],
          seenToolCallIds: new Set(),
          timestamp: event.timestamp || new Date().toISOString(),
          firstEvent: event,
          runId: event.run_id || null,
        };
        for (const b of assistantAccumulator.blocks) {
          if (b?.type === 'tool_call' && b.toolCallId) assistantAccumulator.seenToolCallIds.add(String(b.toolCallId));
        }
        continue;
      }
      for (const b of blocks) {
        if (b?.type === 'tool_call' && b.toolCallId) {
          const key = String(b.toolCallId);
          if (assistantAccumulator.seenToolCallIds.has(key)) continue;
          assistantAccumulator.seenToolCallIds.add(key);
        }
        assistantAccumulator.blocks.push(b);
      }
    }
  }

  flushAssistant();
  return messages;
};

const appendAgentSessionAuthParams = (url, { userProfileId, agentId } = {}) => {
  if (userProfileId) url.searchParams.set('user_profile_id', String(userProfileId));
  if (agentId) url.searchParams.set('agent_id', String(agentId));
};

// ── Unified chat hook ──────────────────────────────────────────────────

/**
 * Unified chat hook for the single agent backend.
 *
 * Supports all features previously split across older per-feature chat hooks
 * (e.g. ontology builder, general agent):
 *   - SSE streaming with sub-agent event display
 *   - Session history, resume, and context usage
 *   - Durable input receipts and resumable, selected-session observation
 *
 * @param {Object} options
 * @param {string} options.usageType `usage_type` filter for context-usage queries.
 */
const ACTIVE_TURN_STATES = new Set(['queued', 'running', 'cancel_requested']);
const makeInputId = () => globalThis.crypto.randomUUID();
const conversationUrl = (path, agentId) => {
  const url = new URL(`${admin_apiConfig.baseURL}/api/conversations/${path}`);
  if (agentId) url.searchParams.set('agent_id', agentId);
  return url;
};

/** Observation state lives only in the selected identity/session. The server owns execution. */
const emptyObservation = () => ({ cursor: 0, replayHighWater: 0, base: [], live: [], runs: new Set(), inputs: new Set(), turns: {}, activeMessage: {} });

const receiptMessage = (input) => {
  const text = input.message || '';
  const blocks = Array.isArray(input.user_blocks)
    ? input.user_blocks.map((block) => normalizeTimelineBlock(block, makeBlockId)).filter(Boolean)
    : [
      ...(text ? [{ id: makeBlockId(), type: 'text', text }] : []),
      ...(input.attachments || []).map((file) => normalizeTimelineBlock({ type: 'attachment', ...file }, makeBlockId)).filter(Boolean),
    ];
  return {
    id: `input-${input.input_id || input.id || input.client_input_id}`,
    role: 'user', content: text, blocks, timestamp: input.accepted_at || input.timestamp || null,
    metadata: { input_id: input.input_id || input.id, client_input_id: input.client_input_id, durable_turn_id: input.turn_id, delivery_status: input.status || 'accepted' },
  };
};

const visibleMessages = (state) => [
  ...state.base.filter((message) => !state.runs.has(message.metadata?.run_id) && !state.inputs.has(message.metadata?.input_id)),
  ...state.live,
];

/** Ordered event reducer: retries/reconnects cannot apply a durable event twice. */
export const applyConversationEvent = (current, { id, eventType, data: event }) => {
  const cursor = Number(id);
  if (!Number.isSafeInteger(cursor) || cursor <= current.cursor) return current;
  const state = { ...current, cursor, live: [...current.live], runs: new Set(current.runs), inputs: new Set(current.inputs), turns: { ...current.turns }, activeMessage: { ...current.activeMessage } };
  const turnId = event.durable_turn_id || event.turn_id;
  const runId = event.run_id || event.metadata?.run_id;
  if (runId) state.runs.add(runId);
  if (eventType === 'input_accepted' || eventType === 'input_received') {
    const inputId = event.input_id || event.id;
    if (inputId) state.inputs.add(inputId);
    const index = state.live.findIndex((message) => message.role === 'user' && (
      (inputId && message.metadata?.input_id === inputId)
      || (event.client_input_id && message.metadata?.client_input_id === event.client_input_id)
    ));
    const status = eventType === 'input_received' ? 'received' : 'accepted';
    if (turnId && !state.turns[turnId]) state.turns[turnId] = { id: turnId, status: 'queued' };
    if (index >= 0) {
      const previous = state.live[index];
      state.live[index] = { ...previous, metadata: { ...previous.metadata, input_id: inputId, delivery_status: previous.metadata?.delivery_status === 'received' ? 'received' : status } };
    } else if (event.message != null || event.user_blocks || event.attachments) {
      state.live.push(receiptMessage({ ...event, status }));
    }
    // A received instruction separates provider segments, without moving output
    // from a request that was already in flight after the instruction.
    if (eventType === 'input_received' && turnId) delete state.activeMessage[turnId];
    return state;
  }
  if (eventType === 'turn_state') {
    state.turns[turnId] = { ...state.turns[turnId], ...event, id: turnId, status: event.status || event.state };
    const status = event.status || event.state;
    if (!ACTIVE_TURN_STATES.has(status)) {
      state.live = state.live.map((message) => message.metadata?.durable_turn_id === turnId
        ? { ...message, isStreaming: false, streamingReasoning: '', blocks: stripTransientStatusBlocks((message.blocks || []).map((block) => block.status === 'running' ? { ...block, status: status === 'succeeded' ? 'completed' : 'interrupted' } : block)) } : message);
      if (['interrupted', 'cancelled', 'failed'].includes(status)) {
        const text = { interrupted: 'This turn was interrupted. Some steps may need review.', cancelled: 'This turn was stopped.', failed: 'This turn could not finish.' }[status];
        state.live.push({ id: `outcome-${turnId}-${cursor}`, role: 'assistant', content: '', timestamp: event.timestamp || event.completed_at || null, blocks: [{ id: makeBlockId(), type: 'notice', text }], metadata: { durable_turn_id: turnId, turn_status: status } });
      }
    }
    return state;
  }
  if (!turnId) return state;
  if (eventType === 'turn_accepted') {
    state.turns[turnId] = { ...state.turns[turnId], id: turnId, status: 'running', run_id: runId };
    if (Array.isArray(event.user_blocks)) {
      const index = state.live.findIndex((message) => message.role === 'user' && (
        event.input_id ? message.metadata?.input_id === event.input_id : message.metadata?.durable_turn_id === turnId
      ));
      if (index >= 0) {
        const blocks = event.user_blocks.map((block) => normalizeTimelineBlock(block, makeBlockId)).filter(Boolean);
        state.live[index] = { ...state.live[index], blocks, content: blocks.filter((block) => block.type === 'text').map((block) => block.text).join('\n') };
      }
    }
    return state;
  }
  const supported = new Set(['text_delta', 'reasoning_delta', 'reasoning_summary_delta', 'tool_call_start', 'tool_call_complete', 'file_artifact', 'agent_card', 'context_compacted', 'model_fallback_notice', 'context_window_exceeded', 'server_status', 'hydration_progress', 'completed', 'error']);
  if (!supported.has(eventType)) return state;
  let index = state.live.findIndex((message) => message.id === state.activeMessage[turnId]);
  if (eventType === 'tool_call_complete') {
    const matchingIndex = state.live.findLastIndex((message) => message.metadata?.durable_turn_id === turnId && message.blocks?.some((block) => block.type === 'tool_call' && block.status === 'running' && (event.tool_call_id ? toolCallIdsMatch(block.toolCallId, event.tool_call_id) : block.toolName === event.tool_name)));
    if (matchingIndex >= 0) index = matchingIndex;
  }
  if (index < 0) {
    const messageId = `turn-${turnId}-${cursor}`;
    state.activeMessage[turnId] = messageId;
    state.live.push({ id: messageId, role: 'assistant', content: '', blocks: [], timestamp: event.timestamp || state.turns[turnId]?.started_at || null, isStreaming: true, metadata: { durable_turn_id: turnId, run_id: runId } });
    index = state.live.length - 1;
  }
  const message = { ...state.live[index], metadata: { ...state.live[index].metadata, ...event.metadata, ...(runId ? { run_id: runId } : {}) } };
  let blocks = [...message.blocks];
  switch (eventType) {
    case 'text_delta':
      blocks = addDeltaBlock(stripTransientStatusBlocks(blocks), 'text', event.delta || '');
      message.content += event.delta || '';
      break;
    case 'reasoning_delta':
    case 'reasoning_summary_delta':
      blocks = addDeltaBlock(stripTransientStatusBlocks(blocks), 'thinking', event.delta || '');
      message.streamingReasoning = (message.streamingReasoning || '') + (event.delta || '');
      break;
    case 'tool_call_start':
      blocks = addToolCallBlock(stripTransientStatusBlocks(blocks), { toolName: event.tool_name, toolCallId: event.tool_call_id, args: event.tool_args, reasoning: event.reasoning });
      break;
    case 'tool_call_complete':
      blocks = completeToolCallBlock(blocks, { toolName: event.tool_name, toolCallId: event.tool_call_id, result: event.result });
      message.streamingReasoning = '';
      break;
    case 'file_artifact':
      blocks.push({ id: makeBlockId(), type: 'attachment', filename: event.filename, contentType: event.content_type, sizeBytes: event.size_bytes, downloadPath: event.download_path, label: event.label, fileRef: event.file_ref });
      break;
    case 'agent_card':
      if (event.card) blocks.push({ id: makeBlockId(), type: 'agent_card', card: event.card });
      break;
    case 'context_compacted':
    case 'model_fallback_notice':
      blocks = addNoticeBlock(blocks, event);
      break;
    case 'server_status':
    case 'hydration_progress':
      blocks = upsertTransientStatusBlock(blocks, event);
      break;
    case 'completed':
      blocks = stripTransientStatusBlocks(finalizeRunningProcessBlocks(blocks));
      // The committed final assistant response can arrive without text deltas
      // (for example after provider recovery). Keep it alongside tool results.
      if (event.response && !message.content.endsWith(event.response)) {
        blocks = addDeltaBlock(blocks, 'text', event.response);
        message.content += event.response;
      }
      message.isStreaming = false;
      message.streamingReasoning = '';
      break;
    case 'context_window_exceeded':
    case 'error':
      message.content = event.error || 'The agent could not finish this turn.';
      blocks = [...stripTransientStatusBlocks(finalizeRunningProcessBlocks(blocks)), { id: makeBlockId(), type: 'error', text: message.content }];
      message.isError = true;
      message.isStreaming = false;
      message.metadata.error_details = event.details || null;
      break;
    default: break;
  }
  message.blocks = blocks;
  state.live[index] = message;
  return state;
};

export const useChat = ({ usageType = 'general_agent_message_context', activePage = '', onToolComplete, agentId = null, observe = true } = {}) => {
  const { data: adminAccount } = useAdminAccount();
  const { data: orgId } = useUserOrgId();
  const { session: authSession } = useAuth();
  const authUserId = authSession?.user?.id;
  const identity = `${authUserId || ''}:${orgId || ''}:${adminAccount?.userProfileId || ''}:${agentId || ''}`;
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [sessionAgentId, setSessionAgentId] = useState(null);
  const [sessionMetadata, setSessionMetadata] = useState(null);
  const [runId, setRunId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [error, setError] = useState(null);
  const [historyError, setHistoryError] = useState(null);
  const [connectionState, setConnectionState] = useState('idle');
  const [latestContextUsage, setLatestContextUsage] = useState(null);
  const [isLoadingContextUsage, setIsLoadingContextUsage] = useState(false);
  const [graphSnapshot, setGraphSnapshot] = useState(null);
  const [isLoadingGraphSnapshot, setIsLoadingGraphSnapshot] = useState(false);
  const [viewAuthoringPreviewState, setViewAuthoringPreviewState] = useState(createViewAuthoringPreviewState());
  const sessionIdRef = useRef(null);
  const observationRef = useRef(emptyObservation());
  const generationRef = useRef(0);
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const resumeAbortControllerRef = useRef(null);
  const observationWakeRef = useRef({ now: null, pending: false });
  const observationAbortRef = useRef(null);
  const graphSnapshotAbortControllerRef = useRef(null);
  const graphSnapshotInFlightRef = useRef(null);
  const viewAuthoringPreviewStateRef = useRef(createViewAuthoringPreviewState());
  const onToolCompleteRef = useRef(onToolComplete);
  onToolCompleteRef.current = onToolComplete;
  const submissionsRef = useRef(new Map());
  const submittingRef = useRef(false);

  const publishObservation = useCallback((next) => {
    observationRef.current = next;
    setMessages(visibleMessages(next));
    setIsStreaming(Object.values(next.turns).some((turn) => ACTIVE_TURN_STATES.has(turn.status)));
  }, []);
  const applyViewAuthoringPreviewUpdate = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(viewAuthoringPreviewStateRef.current) : updater;
    viewAuthoringPreviewStateRef.current = next;
    setViewAuthoringPreviewState(next);
    return next;
  }, []);
  const viewAuthoringPreview = useMemo(() => materializeViewAuthoringPreview(viewAuthoringPreviewState), [viewAuthoringPreviewState]);
  // ── Context usage ────────────────────────────────────────────────────
  const loadLatestContextUsage = useCallback(
    async (targetSessionId, options = {}) => {
      if (!targetSessionId || !adminAccount?.userProfileId) {
        setLatestContextUsage(null);
        return null;
      }
      const maxChatEventSeq = options?.maxChatEventSeq;
      const generation = generationRef.current;
      setIsLoadingContextUsage(true);
      try {
        const url = new URL(
          `${admin_apiConfig.baseURL}${admin_apiConfig.endpoints.agentSessionContextUsage}/${targetSessionId}/context_usage`
        );
        appendAgentSessionAuthParams(url, {
          userProfileId: adminAccount.userProfileId,
          agentId,
        });
        if (usageType) url.searchParams.set('usage_type', usageType);
        if (Number.isFinite(maxChatEventSeq) && maxChatEventSeq > 0) {
          url.searchParams.set('max_chat_event_seq', String(maxChatEventSeq));
        }
        const response = await authFetch(url.toString(), { method: 'GET' });
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || 'Failed to load context usage');
        }
        const payload = await response.json();
        const usage = payload?.usage || null;
        if (generation !== generationRef.current || sessionIdRef.current !== targetSessionId) return null;
        setLatestContextUsage(usage);
        return usage;
      } catch (err) {
        if (generation !== generationRef.current) return null;
        setLatestContextUsage(null);
        return null;
      } finally {
        if (generation === generationRef.current) setIsLoadingContextUsage(false);
      }
    },
    [adminAccount?.userProfileId, agentId, usageType]
  );

  // ── Lightweight session context hydration ────────────────────────────
  const loadViewAuthoringPreview = useCallback(
    async (targetSessionId, { force = false, includeSnapshot = false } = {}) => {
      if (!targetSessionId || !adminAccount?.userProfileId) return null;

      const sessionKey = String(targetSessionId);
      if (graphSnapshotInFlightRef.current?.sessionKey === sessionKey && !force) {
        return graphSnapshotInFlightRef.current.promise;
      }
      if (graphSnapshotAbortControllerRef.current) graphSnapshotAbortControllerRef.current.abort();

      const controller = new AbortController();
      graphSnapshotAbortControllerRef.current = controller;
      setIsLoadingGraphSnapshot(true);
      const requestToken = Symbol('viewAuthoringPreviewRequest');

      const requestPromise = (async () => {
        try {
          const url = new URL(
            `${admin_apiConfig.baseURL}${admin_apiConfig.endpoints.agentSessionViewAuthoringPreview}/${targetSessionId}/view_authoring_preview`
          );
          appendAgentSessionAuthParams(url, {
            userProfileId: adminAccount.userProfileId,
            agentId,
          });
          if (includeSnapshot) url.searchParams.set('include_snapshot', 'true');
          const response = await authFetch(url.toString(), { method: 'GET', signal: controller.signal });
          if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Failed to load view authoring preview');
          }
          const payload = await response.json();

          if (sessionIdRef.current === sessionKey) {
            applyViewAuthoringPreviewUpdate(
              buildViewAuthoringPreviewStateFromEnvironmentSnapshot(payload)
            );
            if (includeSnapshot) setGraphSnapshot(payload?.snapshot ? payload : null);
          }
          return payload;
        } catch (err) {
          if (err?.name === 'AbortError') return null;
          console.warn('Failed to load view preview:', err);
          return null;
        } finally {
          if (graphSnapshotInFlightRef.current?.token === requestToken) {
            graphSnapshotInFlightRef.current = null;
            setIsLoadingGraphSnapshot(false);
          }
        }
      })();

      graphSnapshotInFlightRef.current = { sessionKey, token: requestToken, promise: requestPromise };
      return requestPromise;
    },
    [adminAccount?.userProfileId, agentId, applyViewAuthoringPreviewUpdate]
  );

  const loadSessionPageContext = useCallback(
    async (targetSessionId, { force = false } = {}) => {
      if (!targetSessionId) return null;
      if (isViewAuthoringContextPage(activePage)) {
        return loadViewAuthoringPreview(targetSessionId, { force });
      }
      applyViewAuthoringPreviewUpdate(createViewAuthoringPreviewState());
      return null;
    },
    [activePage, applyViewAuthoringPreviewUpdate, loadViewAuthoringPreview]
  );

  const loadGraphSnapshot = useCallback(
    async (targetSessionId) => {
      if (!targetSessionId || !adminAccount?.userProfileId) return null;
      if (graphSnapshotAbortControllerRef.current) graphSnapshotAbortControllerRef.current.abort();

      const controller = new AbortController();
      graphSnapshotAbortControllerRef.current = controller;
      setIsLoadingGraphSnapshot(true);
      try {
        const url = new URL(
          `${admin_apiConfig.baseURL}${admin_apiConfig.endpoints.agentSessionGraphSnapshot}/${targetSessionId}/graph_snapshot`
        );
        appendAgentSessionAuthParams(url, {
          userProfileId: adminAccount.userProfileId,
          agentId,
        });
        url.searchParams.set('include_snapshot', 'true');
        const response = await authFetch(url.toString(), { method: 'GET', signal: controller.signal });
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || 'Failed to load graph snapshot');
        }
        const payload = await response.json();
        if (sessionIdRef.current === String(targetSessionId)) {
          setGraphSnapshot(payload?.snapshot ? payload : null);
        }
        return payload;
      } catch (err) {
        if (err?.name === 'AbortError') return null;
        console.warn('Failed to load graph snapshot:', err);
        return null;
      } finally {
        setIsLoadingGraphSnapshot(false);
      }
    },
    [adminAccount?.userProfileId, agentId]
  );

  useEffect(() => {
    if (!sessionId || isLoadingHistory || isStreaming) return;
    loadSessionPageContext(sessionId, { runIdHint: runId, force: true });
    void loadLatestContextUsage(sessionId);
  }, [sessionId, runId, activePage, isLoadingHistory, isStreaming, loadSessionPageContext, loadLatestContextUsage]);

  const detachObservation = useCallback(() => {
    observationAbortRef.current?.abort();
    observationAbortRef.current = null;
    resumeAbortControllerRef.current?.abort();
    graphSnapshotAbortControllerRef.current?.abort();
  }, []);

  const createNewSession = useCallback(() => {
    generationRef.current += 1;
    detachObservation();
    sessionIdRef.current = null;
    setSessionId(null);
    setSessionAgentId(null);
    setSessionMetadata(null);
    setRunId(null);
    publishObservation(emptyObservation());
    applyViewAuthoringPreviewUpdate(createViewAuthoringPreviewState());
    setError(null);
    setHistoryError(null);
    setIsLoadingHistory(false);
    setLatestContextUsage(null);
    setIsLoadingContextUsage(false);
    setIsLoadingGraphSnapshot(false);
    setGraphSnapshot(null);
    setConnectionState('idle');
  }, [detachObservation, publishObservation, applyViewAuthoringPreviewUpdate]);

  // Identity changes discard all transcript, pending input and observation state.
  // There is deliberately no agent-only module cache shared between accounts.
  useEffect(() => {
    createNewSession();
    submissionsRef.current.clear();
    return () => { generationRef.current += 1; detachObservation(); };
  }, [identity, createNewSession, detachObservation]);

  const revokeSession = useCallback(() => {
    createNewSession();
    setHistoryError('You no longer have access to that conversation.');
    setConnectionState('forbidden');
  }, [createNewSession]);

  const resumeSession = useCallback(async (targetSessionId) => {
    if (!targetSessionId || !adminAccount?.userProfileId || !agentId) return false;
    createNewSession();
    const generation = generationRef.current;
    const controller = new AbortController();
    resumeAbortControllerRef.current = controller;
    sessionIdRef.current = String(targetSessionId);
    setSessionId(String(targetSessionId));
    setSessionAgentId(agentId);
    setIsLoadingHistory(true);
    try {
      const timelineUrl = new URL(`${admin_apiConfig.baseURL}${admin_apiConfig.endpoints.agentSessionTimeline}/${targetSessionId}/timeline`);
      appendAgentSessionAuthParams(timelineUrl, { userProfileId: adminAccount.userProfileId, agentId });
      timelineUrl.searchParams.set('include_provider_responses', 'false');
      const [history, activity] = await Promise.all([
        authFetch(timelineUrl.toString(), { signal: controller.signal }),
        authFetch(conversationUrl(`${targetSessionId}/activity`, agentId).toString(), { signal: controller.signal }),
      ]);
      if (generation !== generationRef.current) return false;
      if ([history.status, activity.status].some((status) => status === 401 || status === 403 || status === 404)) { revokeSession(); return false; }
      if (!history.ok || !activity.ok) throw new Error('The conversation could not be loaded. Try again.');
      const [payload, status] = await Promise.all([history.json(), activity.json()]);
      if (generation !== generationRef.current) return false;
      const active = payload.active_session || {};
      if (active.agent_id && String(active.agent_id) !== String(agentId)) throw new Error('That conversation belongs to a different agent.');
      const next = emptyObservation();
      next.base = timelineToMessages(payload.timeline || []);
      next.replayHighWater = Number(status.cursor) || 0;
      for (const turn of status.turns || (status.turn ? [status.turn] : [])) next.turns[turn.id || turn.turn_id] = turn;
      publishObservation(next);
      setSessionMetadata(active);
      const resolvedSession = String(active.id || targetSessionId);
      sessionIdRef.current = resolvedSession;
      setSessionId(resolvedSession);
      setRunId(status.turn?.run_id || [...next.base].reverse().find((message) => message.metadata?.run_id)?.metadata.run_id || null);
      return true;
    } catch (err) {
      if (controller.signal.aborted || generation !== generationRef.current) return false;
      setHistoryError(err.message || 'The conversation could not be loaded.');
      return false;
    } finally {
      if (generation === generationRef.current) setIsLoadingHistory(false);
    }
  }, [adminAccount?.userProfileId, agentId, createNewSession, publishObservation, revokeSession]);

  // One selected session observation. Aborting fetch only releases the observer;
  // the durable server worker continues, including when the panel is minimized.
  useEffect(() => {
    if (!observe || !sessionId || isLoadingHistory || historyError || !agentId) return undefined;
    const controller = new AbortController();
    observationAbortRef.current = controller;
    const generation = generationRef.current;
    let reader;
    let retryTimer;
    const current = () => !controller.signal.aborted && generation === generationRef.current && sessionIdRef.current === sessionId;
    const connect = async () => {
      let retryDelay = 750;
      let released = false;
      while (current()) {
        let retryAfter = null;
        try {
          if (!released) setConnectionState(observationRef.current.cursor ? 'reconnecting' : 'connecting');
          // This request will see any input accepted before it is sent.
          observationWakeRef.current.pending = false;
          const url = conversationUrl(`${sessionId}/events`, agentId);
          url.searchParams.set('after', String(observationRef.current.cursor));
          const response = await authFetch(url.toString(), { headers: { Accept: 'text/event-stream' }, signal: controller.signal });
          if (!current()) return;
          if ([401, 403, 404].includes(response.status)) { revokeSession(); return; }
          if (!response.ok || !response.body) throw new Error('Live updates are temporarily unavailable.');
          reader = response.body.getReader();
          setConnectionState('connected');
          for await (const envelope of readSseEvents(reader)) {
            if (!current()) return;
            if (envelope.data?.details?.code === 'conversation_access_revoked') { revokeSession(); return; }
            if (envelope.eventType === 'observer_released') { retryAfter = Number(envelope.data?.retry_after_ms) || 0; continue; }
            const next = applyConversationEvent(observationRef.current, envelope);
            if (next === observationRef.current) continue;
            publishObservation(next);
            const event = envelope.data;
            const eventRunId = event.run_id || event.metadata?.run_id;
            if (eventRunId) setRunId(eventRunId);
            if (envelope.eventType === 'tool_call_complete' && Number(envelope.id) > next.replayHighWater) {
              const toolBlock = next.live.flatMap((message) => message.blocks || []).findLast((block) => block.type === 'tool_call' && (event.tool_call_id ? toolCallIdsMatch(block.toolCallId, event.tool_call_id) : block.toolName === event.tool_name));
              const toolPayload = { toolName: event.tool_name, toolCallId: event.tool_call_id, args: toolBlock?.args || {}, result: event.result, sessionId };
              applyViewAuthoringPreviewUpdate((previous) => applyViewAuthoringToolComplete(previous, toolPayload));
              notifyToolComplete(onToolCompleteRef, toolPayload);
            }
          }
          retryDelay = 750;
        } catch (err) {
          if (!current()) return;
        } finally {
          try { reader?.releaseLock?.(); } catch { /* Reader may already be closed. */ }
          reader = null;
        }
        if (!current()) return;
        // The server ended a healthy observation to release its request slot: stay
        // connected and look again later. A hidden tab waits until someone is looking.
        released = retryAfter !== null;
        if (!released) setConnectionState('reconnecting');
        const wake = observationWakeRef.current;
        await new Promise((resolve) => {
          const finish = () => {
            clearTimeout(retryTimer);
            controller.signal.removeEventListener('abort', finish);
            document.removeEventListener('visibilitychange', onVisible);
            wake.now = null;
            resolve();
          };
          const onVisible = () => { if (!document.hidden) finish(); };
          if (released && wake.pending) { finish(); return; }
          retryTimer = setTimeout(() => { if (!released || !document.hidden) finish(); }, released ? retryAfter : retryDelay);
          controller.signal.addEventListener('abort', finish, { once: true });
          if (released) {
            document.addEventListener('visibilitychange', onVisible);
            wake.now = finish;
          }
        });
        retryDelay = Math.min(retryDelay * 2, 10_000);
      }
    };
    void connect();
    return () => {
      controller.abort();
      void reader?.cancel?.().catch(() => {});
    };
  }, [observe, sessionId, isLoadingHistory, historyError, agentId, revokeSession, publishObservation, applyViewAuthoringPreviewUpdate]);

  const submitInput = useCallback(async (submission) => {
    // Submission and observation have independent lifetimes. A selected-session
    // switch cannot abort an accepted write or make its response select the old session.
    const { clientId, generation, identity: submittedIdentity, expectedUserId, body, session: submittedSession } = submission;
    const stillSelected = () => generation === generationRef.current && identityRef.current === submittedIdentity;
    submittingRef.current = true;
    setIsLoading(true);
    if (stillSelected()) setError(null);
    let response;
    try {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          // A detached write may finish, but a retry must never adopt a new sign-in.
          response = await authFetch(conversationUrl('inputs').toString(), { method: 'POST', body, expectedUserId, headers: { Accept: 'application/json' } });
          if (response.status >= 500 && attempt === 0) continue;
          break;
        } catch (err) { if (attempt === 1) throw err; }
      }
      if (!response.ok) {
        if ([401, 403, 404].includes(response.status) && stillSelected()) {
          revokeSession();
          return { success: false, error: 'Conversation access changed.' };
        }
        const failure = new Error('Your message could not be accepted.');
        failure.definitive = response.status >= 400 && response.status < 500;
        try { const payload = await response.json(); failure.message = typeof payload.detail === 'string' ? payload.detail : failure.message; } catch { /* Keep the safe fallback. */ }
        throw failure;
      }
      const receipt = await response.json();
      submissionsRef.current.delete(clientId);
      if (stillSelected()) {
        const next = { ...observationRef.current, live: observationRef.current.live.map((message) => message.metadata?.client_input_id === clientId ? { ...message, metadata: { ...message.metadata, input_id: receipt.input_id, durable_turn_id: receipt.turn_id, delivery_status: message.metadata?.delivery_status === 'received' ? 'received' : receipt.status || 'accepted' } } : message), turns: { ...observationRef.current.turns } };
        if (receipt.turn_id && !next.turns[receipt.turn_id]) next.turns[receipt.turn_id] = { id: receipt.turn_id, status: 'queued' };
        publishObservation(next);
        // A released observation should not make the reply wait for its next look.
        observationWakeRef.current.pending = true;
        observationWakeRef.current.now?.();
        if (!submittedSession) {
          sessionIdRef.current = receipt.session_id;
          setSessionAgentId(agentId);
          setSessionId(receipt.session_id);
          setSessionMetadata({ id: receipt.session_id, agent_id: agentId, session_kind: 'chat', can_send: true });
        }
      }
      return { success: true, sessionId: receipt.session_id, inputId: receipt.input_id, turnId: receipt.turn_id };
    } catch (err) {
      if (stillSelected()) {
        publishObservation({ ...observationRef.current, live: observationRef.current.live.map((message) => message.metadata?.client_input_id === clientId ? { ...message, metadata: { ...message.metadata, delivery_status: err.definitive ? 'failed' : 'uncertain' } } : message) });
        setError(err.definitive ? err.message : 'Message delivery is unconfirmed. Retry this message to check acceptance safely.');
      }
      return { success: false, error: err.message, clientInputId: clientId };
    } finally {
      submittingRef.current = false;
      setIsLoading(false);
    }
  }, [agentId, publishObservation, revokeSession]);

  const sendMessage = useCallback(async (message, contextData = {}, options = {}) => {
    const files = Array.isArray(options.files) ? options.files.filter(Boolean) : [];
    const text = String(message || '').trim();
    if ((!text && !files.length) || submittingRef.current || isLoadingHistory || historyError || !authUserId || !adminAccount?.userProfileId || !orgId || !agentId) return { success: false };
    if (sessionMetadata?.can_send === false) return { success: false, error: 'You cannot send messages to this conversation.' };
    const clientId = makeInputId();
    const selectedSession = sessionIdRef.current;
    const context = { ...contextData, agent_id: agentId, client_time_zone: deviceTimeZone() };
    // Identity and origin are server-owned; an initiative continuation only
    // identifies its existing session and never supplies a new execution origin.
    for (const field of ['user_profile_id', 'organization_id', 'org_id', 'session_kind', 'origin', 'initiative_id', 'execution_context']) delete context[field];
    if (selectedSession) context.session_id = selectedSession;
    else delete context.session_id;
    const body = new FormData();
    body.append('message', text);
    body.append('context', JSON.stringify(context));
    body.append('client_input_id', clientId);
    files.forEach((file) => body.append('files', file));
    const submission = { clientId, generation: generationRef.current, identity, expectedUserId: authUserId, session: selectedSession, body };
    submissionsRef.current.set(clientId, submission);
    const user = receiptMessage({ client_input_id: clientId, message: text, status: 'sending', attachments: files.map((file) => ({ filename: file.name, contentType: file.type, sizeBytes: file.size })) });
    publishObservation({ ...observationRef.current, live: [...observationRef.current.live, user] });
    return submitInput(submission);
  }, [authUserId, adminAccount?.userProfileId, orgId, agentId, identity, isLoadingHistory, historyError, sessionMetadata?.can_send, publishObservation, submitInput]);

  const retryInput = useCallback((clientId) => {
    const submission = submissionsRef.current.get(clientId);
    if (!submission || submittingRef.current || submission.identity !== identityRef.current || submission.generation !== generationRef.current) return Promise.resolve({ success: false });
    return submitInput(submission);
  }, [submitInput]);

  const cancelStream = useCallback(async () => {
    const selectedSession = sessionIdRef.current;
    const activeTurn = Object.values(observationRef.current.turns).find((turn) => ACTIVE_TURN_STATES.has(turn.status));
    if (!selectedSession || !activeTurn) return false;
    const generation = generationRef.current;
    try {
      const response = await authFetch(conversationUrl(`${selectedSession}/turns/${activeTurn.id || activeTurn.turn_id}/cancel`, agentId).toString(), { method: 'POST' });
      if (!response.ok) throw new Error('The stop request could not be confirmed.');
      // Keep observing until the worker acknowledges its actual terminal state.
      return true;
    } catch (err) {
      if (generation === generationRef.current) setError(err.message);
      return false;
    }
  }, [agentId]);

  const refreshSession = useCallback(() => sessionIdRef.current ? resumeSession(sessionIdRef.current) : Promise.resolve(false), [resumeSession]);
  return {
    messages: sessionAgentId && String(sessionAgentId) !== String(agentId) ? [] : messages, viewAuthoringPreview, isLoading, error, sendMessage, retryInput,
    sessionId, sessionAgentId, sessionMetadata, runId, createNewSession,
    cancelStream, isStreaming, isLoadingHistory, historyError, resumeSession, refreshSession,
    connectionState, isReady: Boolean(authUserId && adminAccount?.userProfileId && orgId && agentId),
    latestContextUsage, isLoadingContextUsage, graphSnapshot, isLoadingGraphSnapshot, loadGraphSnapshot,
  };
};
